/**
 * The split-screen shell every public auth screen sits inside — sign-in and the post-refresh
 * "Continue as" screen alike. Pulled out once both needed it, rather than copied.
 *
 * Two zones on a wide screen: the brand panel on the left carries the shop, the form column on
 * the right carries the one thing to do. On a phone the panel would push the password field off
 * the screen, so it collapses to a short band above the form — the same ground, the same mark,
 * the same name — rather than disappearing and leaving a bare form on white. A sign-in with no
 * brand on it reads as a template, and that is the impression this screen exists to avoid.
 */

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useRememberedShop } from '@pos/shared';
import { brand, neutralBrand } from '@pos/tokens';

import { ShopMark } from '@/shared/ui/ShopMark';

import { BrandPanel } from './BrandPanel';

export function AuthLayout({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  // Applies the remembered shop's look before anyone is identified — or the neutral state on a
  // device that has never signed in. See `useRememberedShop` for what this is and is not.
  const remembered = useRememberedShop();

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateRows: { xs: 'auto 1fr', md: '1fr' },
        // Brand takes the larger share as the screen grows, so the form stays a comfortable
        // reading column instead of stretching with the window.
        gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr', lg: '1.2fr 1fr' },
      }}
    >
      {/* Phone only: the brand band. The panel's ground and mark, at the height of a header. */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          display: { xs: 'flex', md: 'none' },
          bgcolor: 'var(--pos-brand-subtle)',
          color: 'var(--pos-brand-subtle-text)',
          px: 3,
          pt: 'calc(env(safe-area-inset-top) + 20px)',
          pb: 2.5,
        }}
      >
        <ShopMark themeCode={remembered?.themeCode ?? null} neutral={remembered === null} size={30} />
        <Typography sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: 19, lineHeight: 1 }}>
          {remembered?.name ?? neutralBrand.dashboard}
        </Typography>
      </Stack>

      <BrandPanel shop={remembered} />

      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-start', md: 'center' },
          alignItems: 'center',
          bgcolor: 'var(--pos-surface-raised)',
          px: { xs: 3, sm: 6 },
          pt: { xs: 5, md: 8 },
          pb: { xs: 'calc(env(safe-area-inset-bottom) + 40px)', md: 8 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            // The same eight-pixel rise the till uses. The duration token collapses under
            // prefers-reduced-motion, so no media query is needed here either.
            animation: 'pos-rise var(--pos-duration-deliberate) var(--pos-ease-decelerate) both',
            '@keyframes pos-rise': {
              from: { opacity: 0, transform: 'translateY(8px)' },
              to: { opacity: 1, transform: 'none' },
            },
          }}
        >
          <Typography
            component="h1"
            sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, fontSize: { xs: 28, md: 32 } }}
          >
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, mb: 4 }}>
            {description}
          </Typography>

          {children}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 6 }}>
            © {new Date().getFullYear()} {brand.name}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
